import { Injectable } from '@angular/core';
import { CurrentStateService } from '../../../core/services/current-state.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MapComponent } from '../components/map-component/map.component';
import { PopupOpenMode } from '../../control-points/models/popupMode';
import { EventData } from '../../vibration-events/models/event-data';
import { ControlPoint } from '../../../shared/models/controlPoint';
import { MENU_OPTIONS } from '../../../shared/constants/left-menu-option';
import { ControlPointsService } from '../../control-points/services/control-points-service';
import { MapUtilsService } from './map-utils.service';
import { EventService } from '@features/vibration-events/services/event-service';
import { ApiResponse } from '@shared/models/api-response';

@Injectable({
  providedIn: 'root',
})
export class MapSubscriptionService {
  private unsubscribe$ = new Subject<void>();

  constructor(
    private currentStateService: CurrentStateService,
    private controlPointsService: ControlPointsService,
    private mapUtilsService: MapUtilsService,
    private eventService: EventService
  ) { }

  subscribeToCurrentEvent(mapComponent: MapComponent, controlPoints: ControlPoint[]) {
    this.currentStateService.currentEvent$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (event: EventData | null) => {
          if (event) {
            mapComponent.panTo([event.positionCoordinates?.x, event.positionCoordinates?.y], 250, 16);
            if (mapComponent.selectedEvent) {
              const previousFeature = this.mapUtilsService.findFeatureById(
                mapComponent.vectorSourceBaseEvent,
                mapComponent.selectedEvent.id);
              if (previousFeature) {
                previousFeature.setStyle(undefined);
              }
            }
            if (event.id === mapComponent.selectedEvent?.id) {
              mapComponent.selectedEvent = undefined;
              return;
            }

            const selectedFeature = this.mapUtilsService.findFeatureById(
              mapComponent.vectorSourceBaseEvent,
              event.id);
            if (selectedFeature) {
              this.LoadControlPoints(mapComponent.selectedSite?.siteId!, event.id, mapComponent);
              setTimeout(() => {
              this.mapUtilsService.applyStyleToFeature(mapComponent.iconBlast, mapComponent.vectorSourceBaseEvent, event.id);
               }, 600);
              mapComponent.selectedEventFeature = selectedFeature;
            }
            mapComponent.selectedEvent = event;
          } else {
            if (mapComponent.selectedEvent) {
              const previousFeature = this.mapUtilsService.findFeatureById(
                mapComponent.vectorSourceBaseEvent,
                mapComponent.selectedEvent.id);
              if (previousFeature) {
                previousFeature.setStyle(undefined);
              }
            }
            mapComponent.selectedEvent = undefined;
          }
        },
      });
  }

  subscribeToCurrentControlPoint(mapComponent: MapComponent) {
    this.currentStateService.currentControlPoint$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (controlPoint: ControlPoint | null) => {
          if (controlPoint) {
            mapComponent.panTo([controlPoint.x, controlPoint.y], 250, 16);            
            if (mapComponent.selectedControlPoint) {
              const previousFeature = this.mapUtilsService.findFeatureById(
                mapComponent.vectorSourceBasePoint,
                mapComponent.selectedControlPoint.controlPointId);
              if (previousFeature) {
                previousFeature.setStyle(undefined);
              }
              if (controlPoint.controlPointId === mapComponent.selectedControlPoint.controlPointId) {
                mapComponent.selectedControlPoint = undefined;
                return;
              }
            }
            mapComponent.selectedControlPoint = controlPoint;
            const selectedFeature = this.mapUtilsService.findFeatureById(
              mapComponent.vectorSourceBasePoint,
              controlPoint.controlPointId);
            if (selectedFeature) {
              this.loadEvents(mapComponent.selectedSite?.siteId!, controlPoint.controlPointId, mapComponent);
              setTimeout(() => {
              this.mapUtilsService.applyStyleToFeature(mapComponent.iconControlPoint, mapComponent.vectorSourceBasePoint, controlPoint.controlPointId, mapComponent.iconControlPointAlarm);
               }, 600);
              mapComponent.selectedControlPointFeature = selectedFeature;
            }
            mapComponent.selectedControlPoint = controlPoint;
            mapComponent.zoomToPoint();
          } else {
            if (mapComponent.selectedControlPoint) {
              const previousFeature = this.mapUtilsService.findFeatureById(
                mapComponent.vectorSourceBasePoint,
                mapComponent.selectedControlPoint.controlPointId);
              if (previousFeature) {
                previousFeature.setStyle(undefined); // Reset to default style
              }
            }
            mapComponent.selectedControlPoint = undefined; // Clear the selection
          }
        },
      });
  }

  subscribeToOpenPopup(mapComponent: MapComponent) {
    this.currentStateService.openPopup$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (openMode: PopupOpenMode | null) => {
          if (mapComponent.selectedSite) {
            if (openMode === PopupOpenMode.CREATE) {
              this.mapUtilsService.applyStyleToFeature(mapComponent.iconBlast, mapComponent.vectorSourceBaseEvent, -1);
              mapComponent.currentStateService.setCurrentControlPoint(null);
            }

            if (openMode != null) {
              if (mapComponent.currentStateService.getCurrentMenu() === MENU_OPTIONS.CONTROL_POINT) {
                mapComponent.showPopupControlPoint(openMode);
              } else {
                mapComponent.showModalPopupEvent(openMode);
              }
            }
          }
        },
      });
  }

  subscribeToNewControlPointCreated(mapComponent: MapComponent) {
    this.currentStateService.newControlPointCreated$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (newCreatedControlPoint: ControlPoint | null) => {
          if (newCreatedControlPoint) {
            mapComponent.controlPoints = this.controlPointsService.getCachedControlPointsBySiteId(mapComponent.selectedSite?.siteId!);
            this.mapUtilsService.createPointsFeatures(
              mapComponent.iconControlPoint, mapComponent.vectorSourceBasePoint,
              mapComponent.controlPoints);
            mapComponent.refreshControlPointAlarmsByEquipment(mapComponent.selectedSite?.siteId!, mapComponent.controlPoints.map(cp => cp.controlPointId));
          }
        },
      });
  }

  subscribeToNewEventCreated(mapComponent: MapComponent) {
    this.currentStateService.newEventCreated$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (event: EventData | null) => {
          if (event) {
            let siteEvents = mapComponent.currentStateService.getCurrentEvents();
            this.mapUtilsService.createEventsFeatures(
              mapComponent.iconBlast,
              mapComponent.vectorSourceBaseEvent,
              siteEvents);
          }
        },
      });
  }
    LoadControlPoints(siteId: number, eventId: number, mapComponent: MapComponent): void {
    if(siteId === 0 && eventId === 0) {
      this.mapUtilsService.createPointsFeatures(mapComponent.iconControlPoint, mapComponent.vectorSourceBasePoint, []);
      return;
    }
    this.controlPointsService.getControlPointsBySiteId(siteId, false).subscribe({
      next: (points: ControlPoint[]) => {        
        if(eventId > 0){
        this.eventService.getControlPointsByEventId(mapComponent.selectedSite?.siteId!, eventId).subscribe( controlPointIds => {
            let controlPoints = [...points.filter(cp => controlPointIds.data.includes(cp.controlPointId))];
            this.mapUtilsService.createPointsFeatures(mapComponent.iconControlPoint, mapComponent.vectorSourceBasePoint, controlPoints);
            mapComponent.refreshControlPointAlarmsByEquipment(siteId, controlPoints.map(cp => cp.controlPointId));
          })
          }
          else{
            this.mapUtilsService.createPointsFeatures(mapComponent.iconControlPoint, mapComponent.vectorSourceBasePoint, points);
            mapComponent.refreshControlPointAlarmsByEquipment(siteId, points.map(cp => cp.controlPointId));
          }

      },
      error: er => {
      },
    });
  }
    loadEvents(siteId: number,  controlPointId: number, mapComponent: MapComponent) {
        if(siteId == null || siteId === 0) {
          this.mapUtilsService.createEventsFeatures(mapComponent.iconBlast, mapComponent.vectorSourceBaseEvent, []);      
          return;
        }
      this.eventService.getVibrationEvents(siteId, controlPointId).subscribe({
        next: (events: ApiResponse<EventData[]>) => {
          this.currentStateService.setCurrentEvents([...events.data]);
          this.mapUtilsService.createEventsFeatures(mapComponent.iconBlast, mapComponent.vectorSourceBaseEvent, events.data);        
        },
        error: er => {
          console.error('Error init  vibration events:', er);
        },
      });
    }
  unsubscribe() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
